import {
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices'
import {
  MetricSummaryValue as MetricSummaryValueInterface,
  useHistoryMetricSummary
} from '@weenat/client/dist/core/metrics/useMetricSummary'
import { UseMetricSummaryChartInterface } from '@weenat/client/dist/core/metrics/useMetricSummaryChart'
import { isDevice, isPlot, isStation } from '@weenat/client/dist/core/utils/resourcesTypeCheckers'
import { Device, DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { Plot, PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { useIntl } from '@weenat/wintl'
import { DashboardHistoryPeriodState } from 'app/src/devices/types'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import { FlexProps } from 'app/src/kit/primitives/themeMappings/props'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import MetricSummaryValue from './MetricSummaryValue'

interface PeriodBasedMetricSummaryProps {
  /** Metric to display */
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  etpValue?: number | null
  resource: Plot | Device | Station
  isExpertOrg: boolean
  period: DashboardHistoryPeriodState
  prefetch?: UseMetricSummaryChartInterface['prefetch']
  metricSummariesContainerProps?: FlexProps
}

const PeriodBasedMetricSummary: FC<PeriodBasedMetricSummaryProps> = ({
  etpValue,
  metric,
  resource,
  isExpertOrg,
  period,
  prefetch,
  metricSummariesContainerProps
}) => {
  const { t } = useIntl()

  const { isRestricted, reason } = useIsAppAccessRestricted({
    isVWS: isPlot(resource)
      ? undefined
      : (isDevice(resource) && isVirtualWeatherStation(resource)) ||
        (isStation(resource) && isModelVirtualWeatherStation(resource.model))
  })

  const metricData = prefetch?.measurements?.values
  const [{ smv }] = useToggleFeature()

  const { getFullSummaryValue } = useHistoryMetricSummary({
    resource,
    metric,
    isExpertOrg,
    historyData: metricData,
    smv
  })

  const isVirtualSensor = isDevice(resource) && isVirtualWeatherStation(resource)

  const { isPeriodToday, isPeriodYesterday, isPeriodIncludingToday } = useMemo(() => {
    const tz = resource.timezone
    const startOfToday = moment.tz(tz).startOf('day').unix()
    const startOfTomorrow = moment.tz(tz).startOf('day').add(1, 'day').unix()
    const startOfYesterday = moment.tz(tz).startOf('day').add(-1, 'day').unix()
    return {
      isPeriodToday: period.since === startOfToday && period.before === startOfTomorrow,
      isPeriodYesterday: period.since === startOfYesterday && period.before === startOfToday,
      isPeriodIncludingToday: period.since < startOfTomorrow && period.before > startOfToday
    }
  }, [period.since, period.before, resource.timezone])

  const getMetricSummaries = useCallback(
    (
      m: DeviceAvailableMeasure | PlotAvailableMeasure,
      isVWS: boolean
    ): MetricSummaryValueInterface[] => {
      const metricSummaries = []
      switch (m) {
        case 'U':
        case 'T_SOIL':
        case 'T_CAL':
        case 'T_WET':
        case 'T_DRY':
        case 'T':
          if (!isVWS && isPeriodIncludingToday) metricSummaries.push(getFullSummaryValue('latest'))
          if (isVWS || isPeriodYesterday) metricSummaries.push(getFullSummaryValue('average'))
          metricSummaries.push(getFullSummaryValue('min'))
          metricSummaries.push(getFullSummaryValue('max'))
          break
        case 'RR':
          metricSummaries.push(getFullSummaryValue('cumulativeSum'))
          if (!isVWS && isPeriodIncludingToday)
            metricSummaries.push(getFullSummaryValue('untilMidnight'))
          break
        case 'FF':
          if (isPeriodIncludingToday) metricSummaries.push(getFullSummaryValue('latest'))
          if (isPeriodYesterday) metricSummaries.push(getFullSummaryValue('average'))
          metricSummaries.push(getFullSummaryValue('DD'))
          metricSummaries.push(getFullSummaryValue('FXY'))
          break
        case 'SSI':
          if (isPeriodIncludingToday) metricSummaries.push(getFullSummaryValue('latest'))
          if (isPeriodYesterday) metricSummaries.push(getFullSummaryValue('average'))
          metricSummaries.push(getFullSummaryValue('cumulativeHours'))
          break
        case 'T_DEW':
        case 'PPFD':
          if (isPeriodIncludingToday) metricSummaries.push(getFullSummaryValue('latest'))
          if (!isPeriodToday) metricSummaries.push(getFullSummaryValue('average'))
          break
        case 'LW_V':
          if (isPeriodIncludingToday) metricSummaries.push(getFullSummaryValue('latest'))
          metricSummaries.push(getFullSummaryValue('cumulativeHours'))
          break
        case 'etp':
          metricSummaries.push({
            aggregationId: 'etp' as const,
            value: isNil(etpValue) ? null : etpValue,
            label: t('periods.over_the_day', { capitalize: true })
          })
          break
        default:
          break
      }
      return metricSummaries
    },
    [isPeriodIncludingToday, getFullSummaryValue, isPeriodYesterday, isPeriodToday, etpValue, t]
  )

  const metricSummaries = useMemo(
    () => getMetricSummaries(metric, isVirtualSensor),
    [getMetricSummaries, metric, isVirtualSensor]
  )
  return (
    <Box $width={'100%'}>
      <Flex $gap='lg' {...metricSummariesContainerProps}>
        {metricSummaries.map((mSummary) => (
          <MetricSummaryValue
            key={`${mSummary.aggregationId}_${mSummary.label}`}
            metric={metric}
            isMainValue
            isShrunk
            summaryValue={mSummary}
            reason={reason}
            isRestricted={isRestricted}
          />
        ))}
      </Flex>
    </Box>
  )
}

export default PeriodBasedMetricSummary
